import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityTopHeader from '../components/community/CommunityTopHeader';

const TEAL = '#4DB6AC';
const TYPES = ['volunteer', 'learning', 'reading', 'steps', 'environment', 'other'];
const EVENT_TYPES = ['in-person', 'online', 'hybrid'];

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: 14, borderRadius: 12, border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none', fontFamily: 'inherit' };

export default function CommunityCreateChallengePage() {
  const navigate = useNavigate();
  const { token, isAdmin } = useCommunityAuth();
  const [form, setForm] = useState({
    title: '', description: '', challenge_type: 'volunteer', target_value: 5,
    reward_points: 20, instructions: '', geo_tag_required: false, photo_tag: '',
    start_date: '', end_date: '', event_type: 'in-person', venue_details: '', city: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  if (!isAdmin) { navigate('/community'); return null; }

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        ...form,
        instructions: form.instructions.split('\n').filter(Boolean),
        photo_tag: form.photo_tag || null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        venue_details: form.venue_details || null,
        city: form.city || null,
      };
      await axios.post(`${API}/community/challenges`, body, authHeaders);
      navigate('/community');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <CommunityTopHeader showBack />

      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: 16 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input placeholder="Action Title" value={form.title} required data-testid="challenge-title"
            onChange={(e) => set('title', e.target.value)}
            style={inputStyle} />

          <textarea placeholder="Description" value={form.description} required rows={3} data-testid="challenge-description"
            onChange={(e) => set('description', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }} />

          <div>
            <label style={labelStyle}>Challenge Type</label>
            <select value={form.challenge_type} data-testid="challenge-type-select"
              onChange={(e) => set('challenge_type', e.target.value)}
              style={{ ...inputStyle, background: '#fff' }}>
              {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>

          {/* Event Type */}
          <div>
            <label style={labelStyle}>Event Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {EVENT_TYPES.map(t => (
                <button type="button" key={t} data-testid={`event-type-${t}`}
                  onClick={() => set('event_type', t)}
                  style={{
                    flex: 1, padding: '10px 6px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: form.event_type === t ? `${TEAL}18` : '#fff',
                    color: form.event_type === t ? TEAL : '#6B7280',
                    border: `1.5px solid ${form.event_type === t ? TEAL : '#E5E7EB'}`,
                  }}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Start & End Date */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start Date</label>
              <input type="date" value={form.start_date} data-testid="challenge-start-date"
                onChange={(e) => set('start_date', e.target.value)}
                style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>End Date</label>
              <input type="date" value={form.end_date} data-testid="challenge-end-date"
                onChange={(e) => set('end_date', e.target.value)}
                style={inputStyle} />
            </div>
          </div>

          {/* Venue Details */}
          <div>
            <label style={labelStyle}>Venue / Meeting Details</label>
            <input placeholder="e.g. Office Rooftop, Zoom link, etc." value={form.venue_details}
              data-testid="challenge-venue"
              onChange={(e) => set('venue_details', e.target.value)}
              style={inputStyle} />
          </div>

          {/* City */}
          <div>
            <label style={labelStyle}>City</label>
            <input placeholder="e.g. Mumbai, Bangalore" value={form.city}
              data-testid="challenge-city"
              onChange={(e) => set('city', e.target.value)}
              style={inputStyle} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Target</label>
              <input type="number" min={1} value={form.target_value}
                onChange={(e) => set('target_value', parseInt(e.target.value) || 1)}
                style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Reward Points</label>
              <input type="number" min={1} value={form.reward_points}
                onChange={(e) => set('reward_points', parseInt(e.target.value) || 1)}
                style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Instructions (one per line)</label>
            <textarea placeholder="Step 1&#10;Step 2&#10;Step 3" value={form.instructions} rows={4}
              onChange={(e) => set('instructions', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <button type="submit" disabled={submitting} data-testid="submit-challenge"
            style={{ width: '100%', padding: '14px', background: submitting ? '#9CA3AF' : TEAL, color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
            {submitting ? 'Creating...' : 'Create MAD Action'}
          </button>
        </form>
      </div>
    </div>
  );
}
